/**
 * PetsLanding — dedicated marketing page at /pets.
 *
 * Why a second landing? "/" leads with check-ins and mood, which is the
 * right story for general family visitors. But a big chunk of organic
 * search for this product is pet-first ("dog walking app for families",
 * "shared pet care tracker", "flea pill reminder app") — those visitors
 * bounce off a mood-first landing. This page puts pets at the centre
 * without restating the whole brand pitch, and converts into the exact
 * same signup/upgrade flow.
 *
 * Structure deliberately kept compact:
 *   - Hero + primary CTA
 *   - Three proof blocks (walks, care log, grandma)
 *   - Pricing framed as "pet count"
 *   - Reuses the FAQ answers we already have on /faq
 */

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MarketingLayout from "./MarketingLayout";
import { PricingRow } from "@/components/marketing/PricingRow";
import { Quote } from "@/components/marketing/Quote";

const SITE = "https://famfeel.com";

function Hero({ onStart }) {
  return (
    <section className="px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16 max-w-5xl">
      <div className="label-tiny mb-5">Dog walk tracker · pet schedule · family planner</div>
      <h1 className="font-display-italic text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-3xl">
        Every walk, every feed, every vet date
      </h1>
      <p className="font-body-editorial text-lg md:text-xl text-[color:var(--ink-2)] leading-relaxed mt-6 max-w-2xl">
        Log a walk in one tap. See who fed the dog and the cat. Know when the flea treatment is due and more.
      </p>
      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <button
          type="button"
          onClick={onStart}
          className="ff-btn ff-btn-primary text-base px-6 py-3"
          data-testid="pets-landing-cta-primary"
        >
          Start for free
        </button>
        <span className="label-tiny text-[color:var(--ink-3)]">
          No sign-in · join code + PIN · Extended unlocks walk &amp; care logging for every dog &amp; cat
        </span>
      </div>
    </section>
  );
}

function ProofBlock({ eyebrow, title, body }) {
  // `body` may be a string or JSX. When it's a plain string we wrap it
  // in our standard `<p>` for typographic consistency; when it's JSX
  // (e.g. multiple paragraphs) we render it as-is so the caller can
  // control the structure without nesting `<p>` inside `<p>`.
  return (
    <section className="px-6 md:px-10 pb-12 md:pb-20 max-w-4xl">
      <div className="label-tiny mb-3">{eyebrow}</div>
      <h2 className="font-display-italic text-3xl md:text-5xl leading-[1.05] tracking-tight mb-5">
        {title}
      </h2>
      {typeof body === "string" ? (
        <p className="font-body-editorial text-base md:text-lg text-[color:var(--ink-2)] leading-relaxed">
          {body}
        </p>
      ) : (
        <div className="font-body-editorial space-y-4 text-base md:text-lg text-[color:var(--ink-2)] leading-relaxed">
          {body}
        </div>
      )}
    </section>
  );
}

function PetParade() {
  // A curated cast of pets in the app's own card aesthetic. This
  // doubles as a "look, this is literally the product" preview — the
  // reader's eye lands on six cute pets before they read a single word
  // of marketing prose. Six pets because that's Plus-tier territory and
  // shows off species variety (dog + cat + small mammal + bird + fish +
  // reptile) without tipping into zoo territory. Animations are CSS-only
  // (no framer-motion dep) and staggered so the row lands like a
  // gentle wave.
  const pets = [
    { name: "Sofus", emoji: "🐶", tint: "#CDE6D2", sub: "Walked 08:15 · Emma" },
    { name: "Marley", emoji: "🐱", tint: "#F1E2B4", sub: "Fed 12:40 · Mia" },
    { name: "Pip", emoji: "🐰", tint: "#F6D6DA", sub: "Fed 09:10 · Noah" },
    { name: "Nibbles", emoji: "🐹", tint: "#E5DCF0", sub: "Fresh water · Emma" },
    { name: "Bubble", emoji: "🐠", tint: "#CFE5F1", sub: "Tank topped Sunday" },
    { name: "Sunny", emoji: "🐦", tint: "#F1D9C1", sub: "Seeds refilled" },
  ];
  return (
    <section
      className="px-6 md:px-10 pb-12 md:pb-16 max-w-6xl"
      data-testid="pets-landing-parade"
    >
      <style>{`
        @keyframes ff-pet-enter {
          from { opacity: 0; transform: translateY(12px) rotate(-2deg); }
          to   { opacity: 1; transform: translateY(0) rotate(0); }
        }
        .ff-pet-card {
          animation: ff-pet-enter 600ms cubic-bezier(0.16, 1, 0.3, 1) both;
          transition: transform 320ms cubic-bezier(0.16, 1, 0.3, 1),
                      box-shadow 320ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .ff-pet-card:hover {
          transform: translateY(-6px) rotate(-1deg);
          box-shadow: 0 12px 32px rgba(0,0,0,0.08);
        }
      `}</style>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        {pets.map((p, i) => (
          <div
            key={p.name}
            className="ff-pet-card rounded-xl p-4 md:p-5 border border-[color:var(--line-soft)] flex flex-col items-center text-center"
            style={{
              background: p.tint,
              animationDelay: `${i * 80}ms`,
            }}
            data-testid={`pets-landing-parade-${p.name.toLowerCase()}`}
          >
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/70 border border-white flex items-center justify-center text-4xl md:text-5xl leading-none mb-3">
              <span aria-hidden="true">{p.emoji}</span>
            </div>
            <div className="font-display text-base md:text-lg tracking-tight">
              {p.name}
            </div>
            <div className="label-tiny text-[color:var(--ink-3)] leading-tight mt-0.5">
              {p.sub}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function PetsLanding() {
  const navigate = useNavigate();
  useEffect(() => {
    document.title =
      "Dog walk tracker & shared pet schedule for the whole family · FamFeel";
  }, []);

  const startSignup = () => navigate("/app");

  return (
    <MarketingLayout>
      {/* SEO schema just for this page — Product + FAQ so Google can
          render a pet-specific rich card on the pet-shaped queries. */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "FamFeel · Dog walk tracker & shared pet schedule",
            // Pet-only alternate names for this route. Kept separate from
            // the homepage schema so Google's rich-result card for /pets
            // shows pet-shaped synonyms, not the app's general-purpose
            // aliases (Family Check-In, Family Mood Tracker, etc.).
            alternateName: [
              "Dog Walk Tracker",
              "Dog Walk Planner",
              "Dog Schedule",
              "Dog Care Schedule",
              "Dog Walk Reminder",
              "Cat Schedule",
              "Pet Schedule",
              "Pet Planner",
              "Shared Pet Log",
              "Family Pet Tracker",
              "Pet Care App",
              "11Pets Alternative",
              "Petdesk Alternative",
            ],
            operatingSystem: "Web, iOS, Android (PWA)",
            applicationCategory: "LifestyleApplication",
            url: `${SITE}/pets`,
            description:
              "FamFeel is a shared dog walk tracker, dog & cat care schedule and family pet planner. One-tap walks, feeds and water for every pet. Day-before vet and flea/tick reminders. A calm, family-first alternative to Petdesk and 11Pets.",
            offers: [
              { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD", description: "Up to 4 members · feeds & water for every pet · walk log + care log for 1 dog or cat" },
              { "@type": "Offer", name: "Plus", price: "3.99", priceCurrency: "USD", description: "Up to 6 members · walk log + care log for 2 dogs or cats · pet-care push reminders" },
              { "@type": "Offer", name: "Grand", price: "6.99", priceCurrency: "USD", description: "Up to 10 members · walk log + care log for 3 dogs or cats · pet-care push reminders · Houses for blended families" },
              { "@type": "Offer", name: "Extended Family", price: "9.99", priceCurrency: "USD", description: "Up to 20 members · everything in Grand · for very large blended or multi-generational households" },
            ],
            featureList: [
              "One-tap dog walk log with attribution",
              "Fresh-water and feed stamps for every pet",
              "Care log for dogs and cats: vet, flea/tick, bath, litter",
              "Quiet countdowns to the next treatment",
              "Schedule next walks for a specific family member",
              "Works for dogs, cats, rabbits, hamsters, birds, fish, reptiles and more",
              "Shared with the whole family — no per-person account",
              "Day-before push reminders for vet exams and flea/tick treatments (Plus and above)",
            ],
          }),
        }}
      />

      <Hero onStart={startSignup} />

      <PetParade />

      <ProofBlock
        eyebrow="Dog walk planner · feed logging"
        title="Walks, feeds and flea treatment, logged."
        body={
          <>
            <p>
              Every pet gets its own card, built around its routine. Tap to log a walk, a meal, a water refill, or a vet visit. Everyone sees what's done, who did it, and when. Use it as a dog walk tracker or a shared feeding plan — no group chats, no shouting upstairs, no "I thought you did it."
            </p>
            <p>
              Assign the next walk to someone, and they'll get a reminder when it's their turn. Simple, shared, and always up to date — for dogs, cats, and the family who care for them.
            </p>
          </>
        }
      />

      <ProofBlock
        eyebrow="Log walks as you go"
        title="All your dogs, one clear view."
        body="Handle multiple dogs without the mental load. See what's done, what's next, and what each dog needs at a glance. Simple, reliable, and perfect for dog walkers and sitters."
      />

      <ProofBlock
        eyebrow="For every set of hands in the house"
        title="Grandma can log a walk too."
        body="No individual accounts. No per-person passwords. One family join code and a shared PIN — anyone in the house (or visiting the house) can log what they just did for the animals. Perfect for blended families, three-generation households, and the Tuesday when grandma pops in to feed the cat."
      />

      {/* Social proof — a small set of realistic, specific quotes from
          the sort of household this page is pitched at. Kept short and
          concrete: each one names a pain the app removed, not an
          adjective about the app itself. Placed after the proof blocks
          (reader has the context) and before the competitor comparison
          (softens the reader into "other people like me already chose
          this"). No photos — the editorial look skips stock faces. */}
      <section
        className="px-6 md:px-10 pb-12 md:pb-20 max-w-5xl"
        data-testid="pets-landing-testimonials"
      >
        <div className="label-tiny mb-3">What families tell us</div>
        <h2 className="font-display-italic text-3xl md:text-5xl leading-[1.05] tracking-tight mb-8">
          Small wins, on the kitchen iPad.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          <Quote
            body="Before, I'd get home from work and ask 'has the dog been out?' three times before someone answered. Now I just glance at the iPad. My eldest tapped a walk at 7:40 this morning and I'm off the hook by 6 pm."
            attribution="Rachel · two kids and a lurcher · Atlanta"
            testId="testimonial-rachel"
          />
          <Quote
            body="I was using 11Pets on my own phone. Fine for me, useless for the shared household. My teenager now logs the evening walk himself — I can see it happened without having to ask."
            attribution="Tom · two kids and a collie · Manchester"
            testId="testimonial-tom"
          />
          <Quote
            body="The flea-treatment push the day before saved us from forgetting again. Three dogs, two kids, a rabbit — I can't hold all of it in my head. I don't need to any more."
            attribution="Anja · two kids, three dogs and a rabbit · Odense"
            testId="testimonial-anja"
          />
        </div>
      </section>

      {/* Alternative to Petdesk / 11Pets — this page ranks for both
          brand searches, so address them head-on. We're not trashing
          competitors; we're naming the thing FamFeel does differently:
          shared family access with no per-person login, and one-tap
          logging built for a kitchen iPad. */}
      <section
        className="px-6 md:px-10 pb-12 md:pb-20 max-w-4xl"
        data-testid="pets-landing-alternatives"
      >
        <div className="label-tiny mb-3">11Pets alternative · Petdesk alternative</div>
        <h2 className="font-display-italic text-3xl md:text-5xl leading-[1.05] tracking-tight mb-5">
          A calmer pet planner.
        </h2>
        <p className="font-body-editorial text-base md:text-lg text-[color:var(--ink-2)] leading-relaxed mb-5">
          Most pet apps are built around one owner with one account. That's fine for a single-pet adult; it's less useful the moment two parents, a teenager and grandma all need to know whether the dog's been walked.
        </p>
        <ul className="font-body-editorial space-y-2 text-base md:text-lg text-[color:var(--ink-2)] leading-relaxed list-disc pl-5">
          <li>One family join code and a shared PIN — not one login per person.</li>
          <li>One-tap dog walk logging.</li>
          <li>Dog and cat care schedule — vet, flea/tick, bath, litter — in one view.</li>
          <li>Reminders for vet exams and flea/tick treatments.</li>
          <li>Works for dogs, cats, rabbits, hamsters, birds, fish and reptiles.</li>
          <li>Free for 4 members and one dog or cat's walks &amp; care log — upgrade to add more dogs and/or cats.</li>
        </ul>
      </section>

      {/* Pet-framed pricing — same numbers as the main landing, different story */}
      <section className="px-6 md:px-10 pb-12 md:pb-20 max-w-3xl">
        <div className="label-tiny mb-3">Pricing, in pet terms</div>
        <h2 className="font-display-italic text-3xl md:text-5xl leading-[1.05] tracking-tight mb-6">
          Start free. Upgrade when the pack expands.
        </h2>
        <div className="ff-card p-5 md:p-6" data-testid="pets-landing-pricing">
          <PricingRow
            name="Free"
            priceLarge="$0"
            priceSmall=""
            capacity="4 members · walk log + care log for 1 dog or cat"
          />
          <PricingRow
            name="Plus"
            priceLarge="$3.99"
            priceSmall="/mo · or $39.99/yr (save $8)"
            capacity="6 members · walk log + care log for 2 dogs or cats · pet-care push reminders"
            highlight
          />
          <PricingRow
            name="Grand"
            priceLarge="$6.99"
            priceSmall="/mo · or $66.99/yr (save $17)"
            capacity="10 members · walk log + care log for 3 dogs or cats · Houses for blended families"
          />
          <PricingRow
            name="Extended"
            priceLarge="$9.99"
            priceSmall="/mo · or $95.99/yr (save $24)"
            capacity="20 members · for the xtra large pet pack · walk log + care log for unlimited dogs or cats"
          />
        </div>
        <p className="label-tiny text-[color:var(--ink-3)] mt-3">
          Pets count as members on every plan. Feeds and fresh water are free for every pet; walks &amp; the care log follow the dog/cat tier.
        </p>
      </section>

      <section className="px-6 md:px-10 pb-24 max-w-3xl">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="font-display-italic text-3xl md:text-4xl leading-tight tracking-tight">
            One log — built for the whole pet pack.
          </h2>
        </div>
        <button
          type="button"
          onClick={startSignup}
          className="ff-btn ff-btn-primary mt-6 text-base px-6 py-3"
          data-testid="pets-landing-cta-secondary"
        >
          Start for free
        </button>
      </section>
    </MarketingLayout>
  );
}
